import { LDClient } from 'launchdarkly-react-client-sdk'

import {
  AutoAwesomeOutlined,
  AutoStoriesOutlined,
  CasesOutlined,
  FavoriteBorder,
  GroupAddOutlined,
  HomeOutlined,
  ListAltOutlined,
  LogoutOutlined,
  PaidOutlined,
  PeopleOutlined,
  PersonAddAltOutlined,
  PersonOutlineOutlined,
  PieChartOutlineOutlined,
  RequestPageOutlined,
  SettingsOutlined,
  SupportAgentOutlined,
  UpgradeOutlined,
  WorkspacePremiumOutlined,
  WorkspacesOutlined
} from '@mui/icons-material'

import { i18nTranslationHelper } from '@/i18n'
import { AccountType } from '@/data/usecases/account/account-type'
import { FeatureFlagName, getFlagValue } from '@/infra/feature-flag'
import {
  makeSkillWebCompanyRedirectUrl,
  makeSkillWebManagerTeamRedirectUrl,
  makeSkillWebRequestsRedirectUrl
} from '@/main/factories/http'
import PathConstants from '@/main/routes/paths'
import { AccountWithCustomProperties } from '@/presentation/contexts/auth'
import { showDependentRequestButton } from '@/presentation/helpers/dependent-request-helper'
import { showUpgradeRequestButton } from '@/presentation/helpers/upgrade-request-helper'

import { MenuItemDefinition, MenuSectionDefinition } from './types'

const { Routes } = PathConstants
const t = i18nTranslationHelper.getTComponents

export const getMenuHome = (): MenuItemDefinition => {
  return {
    key: 'home',
    route: Routes.HOME,
    icon: HomeOutlined,
    isVisible: true
  }
}

export const getMenuHelpCenter = (
  account: AccountWithCustomProperties
): MenuSectionDefinition | null => {
  if (!account?.helpCenterConfig?.hasLink) return null

  return {
    items: [
      {
        key: 'help_center',
        route: {
          title: t('helpCenterButtonApp.cta'),
          value: account.helpCenterConfig.redirectTo,
          name: 'help'
        },
        icon: SupportAgentOutlined,
        isVisible: true
      }
    ]
  }
}

export const getMenuMyProfile = (
  account: AccountWithCustomProperties,
  ldClient?: LDClient
): MenuSectionDefinition => {
  const isAccountCollaborator = AccountType.Enum.COLLABORATOR.is(account)
  const isAccountDependent = AccountType.Enum.DEPENDENT.is(account)
  const isAddonRefundEnabled = !!account.addons?.refund.enabled
  const hasPassPlan = !!account?.passPlan

  const isUserSpaceEnabled = getFlagValue(FeatureFlagName.USER_SPACE_ENABLED, ldClient)

  const isDependentRequestButton = showDependentRequestButton(account)
  const isUpgradeRequestButton = showUpgradeRequestButton(account)

  return {
    titleKey: 'navUserMenu.listTitle',
    items: [
      {
        key: 'user_profile',
        route: Routes.USER_PROFILE,
        icon: PersonOutlineOutlined,
        isVisible: isAccountCollaborator || isAccountDependent
      },
      {
        key: 'user_space',
        route: Routes.USER_SPACE,
        icon: WorkspacePremiumOutlined,
        isVisible: isUserSpaceEnabled
      },
      {
        key: 'user_enrollments',
        route: Routes.USER_ENROLLMENTS_V2,
        icon: AutoStoriesOutlined,
        isVisible: true
      },
      {
        key: 'user_interest_areas_skills',
        route: Routes.USER_INSTEREST_AREAS_SKILLS,
        icon: AutoAwesomeOutlined,
        isVisible: hasPassPlan
      },
      {
        key: 'user_favorites',
        route: Routes.FAVORITES,
        icon: FavoriteBorder,
        isVisible: true
      },
      {
        key: 'upgrade_request',
        route: { ...Routes.UPGRADE_REQUEST, title: t('mobileUpgradeRequestButton.cta') },
        icon: UpgradeOutlined,
        className: 'only-mobile',
        isVisible: isUpgradeRequestButton
      },
      {
        key: 'dependent_request',
        route: { ...Routes.DEPENDENT_REQUEST, title: t('mobileDependentRequestButton.cta') },
        icon: PersonAddAltOutlined,
        className: 'only-mobile',
        isVisible: isDependentRequestButton,
        onClick: () => sessionStorage.removeItem('dependentRequestForm')
      },
      {
        key: 'user_refund',
        route: { ...Routes.REFUND, title: t('mobileUserRefundButton.cta') },
        icon: PaidOutlined,
        className: 'only-mobile',
        isVisible: isAddonRefundEnabled
      },
      {
        key: 'user_settings',
        route: Routes.USER_SETTINGS,
        icon: SettingsOutlined,
        isVisible: true
      }
    ]
  }
}

export const getMenuMyTeam = (): MenuSectionDefinition => {
  return {
    titleKey: 'navUserMenu.teamManagerListTitle',
    items: [
      {
        key: 'team_manager_authorizations',
        route: {
          value: makeSkillWebManagerTeamRedirectUrl(),
          title: t('navUserMenu.orderTitle'),
          name: 'monolith_team_manager_authorizations'
        },
        icon: ListAltOutlined,
        isVisible: true
      }
    ]
  }
}

export const getMenuMyCompany = (
  account: AccountWithCustomProperties,
  ldClient?: LDClient
): MenuSectionDefinition => {
  const useNewCollaboratorsFlow = getFlagValue(
    FeatureFlagName.ENABLE_NEW_COLLABORATORS_FLOW,
    ldClient
  )
  const useNewAuthorizationsFlow = getFlagValue(
    FeatureFlagName.ENABLE_NEW_AUTHORIZATIONS_FLOW,
    ldClient
  )
  return {
    titleKey: 'navUserMenu.companyManagerListTitle',
    items: [
      {
        key: 'company_manager_dashboard',
        route: Routes.COMPANY_MANAGER_DASHBOARD,
        icon: PieChartOutlineOutlined,
        isVisible: true
      },
      {
        key: 'company_manager_manage_customers',
        route: useNewCollaboratorsFlow
          ? Routes.COMPANY_MANAGER_COLLABORATORS
          : Routes.MONOLITH_MANAGE_CUSTOMER,
        icon: PeopleOutlined,
        availableIn: [Routes.COMPANY_MANAGER_EDIT_USER_PROFILE],
        isVisible: true
      },
      {
        key: 'company_manager_dependent_list',
        route: Routes.COMPANY_MANAGER_DEPENDENT_LIST,
        icon: GroupAddOutlined,
        availableIn: [
          Routes.COMPANY_MANAGER_EDIT_DEPENDENT_USER_PROFILE,
          Routes.COMPANY_MANAGER_HOLDER_SEARCH,
          Routes.COMPANY_MANAGER_DEPENDENT_REGISTRATION
        ],
        isVisible: true
      },
      {
        key: 'company_manager_authorizations',
        route: useNewAuthorizationsFlow
          ? Routes.COMPANY_MANAGER_AUTHORIZATIONS
          : {
              value: makeSkillWebRequestsRedirectUrl(),
              title: t('navUserMenu.orderTitle'),
              name: 'monolith_company_manager_authorizations'
            },
        icon: ListAltOutlined,
        isVisible: true
      },
      {
        key: 'company_manager_groups',
        route: Routes.COMPANY_MANAGER_GROUPS,
        icon: WorkspacesOutlined,
        isVisible: true
      },
      {
        key: 'company_manager_company',
        route: {
          value: makeSkillWebCompanyRedirectUrl(account.company.uniqueId),
          title: t('navUserMenu.companyTitle'),
          name: 'monolith_company_manager_company'
        },
        icon: CasesOutlined,
        isVisible: true
      },
      {
        key: 'company_manager_requests',
        icon: RequestPageOutlined,
        isVisible: true,
        titleKey: 'navUserMenu.requestsTitle',
        items: [
          {
            key: 'company_manager_upgrade_enrollment',
            route: Routes.COMPANY_MANAGER_UPGRADE_ENROLLMENT,
            icon: UpgradeOutlined,
            isVisible: true
          },
          {
            key: 'company_manager_dependent_enrollment',
            route: Routes.COMPANY_MANAGER_DEPENDENT_ENROLLMENT,
            icon: PersonAddAltOutlined,
            isVisible: true
          },
          {
            key: 'company_manager_requests_configuration',
            route: Routes.COMPANY_MANAGER_REQUESTS_CONFIGURATION,
            icon: SettingsOutlined,
            isVisible: true
          }
        ]
      }
    ]
  }
}

export const getMenuLogout = (): MenuSectionDefinition => {
  return {
    items: [
      {
        key: 'logout',
        route: {
          value: Routes.LOGOUT.value,
          title: t('navUserMenu.logoutCta'),
          name: 'monolith_logout'
        },
        icon: LogoutOutlined,
        isVisible: true
      }
    ]
  }
}
